import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { ShieldCheck, Loader2, KeyRound } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAdminAuth } from "@/context/AdminAuthContext";

const AdminLogin = () => {
  const { admin, login, verifyOtp, pendingOtpEmail, devOtp } = useAdminAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  if (admin) return <Navigate to="/admin/dashboard" replace />;

  const submitCredentials = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    const r = await login(email, password);
    setBusy(false);
    if (!r.ok) setError(r.error);
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    const r = await verifyOtp(pendingOtpEmail, otp);
    setBusy(false);
    if (r.ok) navigate("/admin/dashboard", { replace: true });
    else setError(r.error);
  };

  return (
    <div className="min-h-screen grid place-items-center bg-[#FFFBF5] font-sans" data-testid="admin-login-page">
      <div className="w-full max-w-md px-6">
        <div className="text-center mb-8">
          <div className="mx-auto inline-grid place-items-center h-14 w-14 rounded-2xl bg-[#0D1B2A] text-[#E85D04] shadow-lg">
            <ShieldCheck className="h-6 w-6" />
          </div>
          <div className="mt-4 text-xs font-semibold uppercase tracking-[0.2em] text-[#E85D04]">Dwinov Admin</div>
          <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight text-[#0D1B2A]">
            {pendingOtpEmail ? "Enter your sign-in code" : "Sign in to your admin console"}
          </h1>
          <p className="mt-2 text-sm text-[#0D1B2A]/60">
            {pendingOtpEmail ? `We sent a 6-digit code to ${pendingOtpEmail}.` : "Access is restricted to authorized staff only."}
          </p>
        </div>

        <div className="rounded-2xl bg-white p-8 border border-[#0D1B2A]/10 shadow-sm">
          {!pendingOtpEmail ? (
            <form onSubmit={submitCredentials} className="space-y-4" data-testid="admin-credentials-form">
              <div>
                <Label htmlFor="a-email" className="text-[#0D1B2A]">Email</Label>
                <Input id="a-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  data-testid="admin-email-input"
                  className="mt-1.5 h-11 rounded-xl border-[#0D1B2A]/15 bg-white focus-visible:ring-[#E85D04] focus-visible:border-[#E85D04]" />
              </div>
              <div>
                <Label htmlFor="a-password" className="text-[#0D1B2A]">Password</Label>
                <Input id="a-password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
                  data-testid="admin-password-input"
                  className="mt-1.5 h-11 rounded-xl border-[#0D1B2A]/15 bg-white focus-visible:ring-[#E85D04] focus-visible:border-[#E85D04]" />
              </div>
              {error && <div className="text-sm text-red-600" data-testid="admin-error">{error}</div>}
              <button type="submit" disabled={busy} data-testid="admin-continue-btn"
                className="w-full h-11 rounded-xl bg-[#E85D04] hover:bg-[#c94d02] text-white font-semibold transition-colors disabled:opacity-70">
                {busy ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : "Continue"}
              </button>
            </form>
          ) : (
            <form onSubmit={submitOtp} className="space-y-4" data-testid="admin-otp-form">
              <div className="flex items-center gap-3 rounded-xl bg-[#FFFBF5] px-3 py-2 border border-[#0D1B2A]/10">
                <KeyRound className="h-4 w-4 text-[#E85D04]" />
                <div className="text-xs text-[#0D1B2A]/70">6-digit code · expires in 10 minutes</div>
              </div>
              <div>
                <Label htmlFor="a-otp" className="text-[#0D1B2A]">One-time code</Label>
                <Input id="a-otp" required inputMode="numeric" pattern="\d{6}" maxLength={6}
                  value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  data-testid="admin-otp-input"
                  className="mt-1.5 h-14 text-center text-2xl tracking-[0.6em] font-mono rounded-xl border-[#0D1B2A]/15 focus-visible:ring-[#E85D04]" />
              </div>
              {devOtp && (
                <div className="text-xs rounded-xl bg-amber-50 border border-amber-200 text-amber-900 px-3 py-2" data-testid="admin-dev-otp">
                  Dev mode (RESEND_API_KEY not set): use code <strong className="font-mono">{devOtp}</strong>
                </div>
              )}
              {error && <div className="text-sm text-red-600" data-testid="admin-error">{error}</div>}
              <button type="submit" disabled={busy || otp.length !== 6} data-testid="admin-verify-btn"
                className="w-full h-11 rounded-xl bg-[#E85D04] hover:bg-[#c94d02] text-white font-semibold transition-colors disabled:opacity-70">
                {busy ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : "Verify & sign in"}
              </button>
            </form>
          )}
        </div>
        <p className="mt-6 text-center text-xs text-[#0D1B2A]/40">Dwinov • Admin Console</p>
      </div>
    </div>
  );
};

export default AdminLogin;
